import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import { Icon, type IconName } from '@/shared/icons';
import { FLAME_COLORS, getLeadInitials, type LeadCardViewModel } from './leadCardHelpers';

export interface LeadCardAvatarProps {
  /** 20 (compacto) · 24 (contato de Detalhes / responsável do Normal) · 28 (contato do Normal). */
  size: 20 | 24 | 28;
  name?: string;
  picture?: string;
  /** `assignee` usa "Responsável: …" no tooltip e habilita a marca de papel via `ring`. */
  role: 'contact' | 'assignee';
  /**
   * Marca de papel do responsável (`.c3-avatar--owner`). **Opt-in por variante**, não
   * automática: a fundação (§9) dispensa o recorte quando o card já traz um RÓTULO
   * textual de papel, e o mockup corrigido segue isso à risca — o Detalhes tem o rótulo
   * "Responsável" acima do avatar e fica SEM anel (`crm-corrigidos.html:216`), enquanto o
   * Normal (`:262`) e o Compacto (`:366`) não têm rótulo nenhum e levam o anel. Só faz
   * efeito com `role="assignee"`.
   */
  ring?: boolean;
  'data-testid'?: string;
}

/** Lettering do mockup §9: 28→11px, 24→10px, 20→9px (9 é o piso). */
const INITIALS_FONT: Record<20 | 24 | 28, number> = { 20: 9, 24: 10, 28: 11 };

export const LeadCardAvatar: React.FC<LeadCardAvatarProps> = ({
  size, name, picture, role, ring = false, 'data-testid': testId,
}) => {
  // Sem responsável: contorno tracejado + ícone de PESSOA (`.c3-avatar--empty`).
  // Estado de ausência, não é pessoa — não leva marca de papel nem cor de
  // identidade. O ícone é `user`, e não o `+` do mockup, por decisão de
  // unificação: os três módulos (CRM, Atendimento, Helpdesk) desenham o mesmo
  // vazio — mesmo tracejado, mesmo ícone de pessoa e `Tooltip` shared (nunca
  // `title`). Três desenhos diferentes para "ninguém responde" era o defeito.
  if (role === 'assignee' && !name) {
    return (
      <Tooltip content="Sem responsável">
        <span
          className="inline-flex flex-none items-center justify-center"
          data-testid={testId}
          style={{
            width: size, height: size, borderRadius: '32%',
            border: '1px dashed var(--theme-input-border)',
            color: 'var(--theme-muted-text)',
          }}
        >
          <Icon name="user" size={12} />
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={role === 'assignee' ? `Responsável: ${name}` : (name || 'Sem nome')}>
      <span
        className="relative inline-flex flex-none"
        data-testid={testId}
        style={{ width: size, height: size }}
      >
        <Avatar className="w-full h-full" isSquircle>
          {picture && <AvatarImage src={picture} alt={name || ''} isSquircle />}
          <AvatarFallback isSquircle>
            <span
              className="text-white font-semibold"
              style={{ fontSize: INITIALS_FONT[size], lineHeight: 1 }}
            >
              {getLeadInitials(name)}
            </span>
          </AvatarFallback>
        </Avatar>
        {/* Marca de papel do responsável (`.c3-avatar--owner`: outline 1px, offset 2px).
            NÃO pode ser `outline`/`box-shadow` no próprio Avatar: o shared aplica
            `mask is-squircle` (index.css:64) e a máscara recorta tudo que é pintado
            fora da caixa. Anel absoluto num wrapper sem máscara reproduz o desenho e
            não entra no layout — importante no Compacto, onde a linha tem 50px. */}
        {role === 'assignee' && ring && (
          <span
            aria-hidden
            className="absolute pointer-events-none"
            style={{ inset: -3, borderRadius: '32%', border: '1px solid var(--theme-input-border)' }}
          />
        )}
      </span>
    </Tooltip>
  );
};

export const LeadCardFlame: React.FC<{ temperature: 'hot' | 'warm' | 'cold'; size: 12 | 14; leadId: string }> = ({
  temperature, size, leadId,
}) => (
  <Tooltip content={`Temperatura: ${temperature === 'hot' ? 'quente' : temperature === 'warm' ? 'morno' : 'frio'}`}>
    <span className="flex-none inline-flex" data-testid={`lead-flame-${leadId}`}>
      <Icon name="flame" size={size} style={{ color: FLAME_COLORS[temperature] }} />
    </span>
  </Tooltip>
);

export interface LeadCardFieldProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'color'> {
  label: string;
  icon: IconName;
  value: string;
  /** CSS variable — cor do texto e, por padrão, do ícone. */
  color: string;
  /** Quando o ícone é metadado e não carrega o tom (canal). */
  iconColor?: string;
  'data-testid'?: string;
}

/**
 * Campo do `.crm1-grid`: rótulo `.c3-overline` (11/600/0.06em) + valor 12/600.
 *
 * É `forwardRef` e repassa o resto das props para a raiz porque o campo "Parado"
 * é o GATILHO do `LeadAlertTooltip` no corpo de Detalhes. O `Tooltip` shared usa
 * `TooltipTrigger asChild` → `Slot`, que faz `cloneElement(child, { ...handlers,
 * ref })`: um componente de função sem `forwardRef`/spread engole o `ref` e os
 * `onPointerEnter`/`onFocus` do Radix, e o tooltip simplesmente nunca abre (sem
 * erro, só o aviso "Function components cannot be given refs"). Nenhum nó extra
 * no DOM — a própria célula da grade é o gatilho.
 */
export const LeadCardField = React.forwardRef<HTMLDivElement, LeadCardFieldProps>(({
  label, icon, value, color, iconColor, className, ...rest
}, ref) => (
  <div ref={ref} className={className ? `min-w-0 ${className}` : 'min-w-0'} {...rest}>
    <div
      className="truncate"
      style={{
        fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
        textTransform: 'uppercase', lineHeight: 1.2, color: 'var(--theme-muted-text)',
      }}
    >
      {label}
    </div>
    <div
      className="flex items-center min-w-0"
      style={{ marginTop: 4, gap: 4, fontSize: 12, fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }}
    >
      <Icon name={icon} size={14} style={{ color: iconColor ?? color, flex: 'none' }} />
      <span className="truncate min-w-0">{value}</span>
    </div>
  </div>
));
LeadCardField.displayName = 'LeadCardField';

/**
 * Envelope do tooltip de alerta. O gatilho é o ELEMENTO QUE CARREGA O ALERTA em
 * cada variante (campo "Parado", meta do rodapé, ponto do Compacto) — ver a
 * justificativa na tarefa 16, passo 3.
 */
export const LeadAlertTooltip: React.FC<{ vm: LeadCardViewModel; children: React.ReactElement }> = ({
  vm, children,
}) => {
  if (!vm.alertTooltip) return children;
  const { title, titleColor, body, hint } = vm.alertTooltip;
  return (
    <Tooltip
      side="bottom"
      content={
        <div className="max-w-[240px]" data-testid={`lead-alert-tooltip-${vm.id}`}>
          <div className="text-[11px] font-bold uppercase tracking-wide mb-1" style={{ color: titleColor }}>
            {title}
          </div>
          <div className="text-xs leading-relaxed">{body}</div>
          {hint && (
            <div
              className="text-[11px] leading-relaxed mt-1.5 pt-1.5"
              style={{ color: '#94a3b8', borderTop: '1px solid rgba(255,255,255,0.12)' }}
            >
              {hint}
            </div>
          )}
        </div>
      }
    >
      {children}
    </Tooltip>
  );
};
